import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, CalendarClock, FileText, Award, Presentation, TrendingUp } from "lucide-react";
import { getStudentDashboard } from "@/lib/api/student-portal.functions";
import { profileCompletion, qualificationLabel, folderLabel } from "@/lib/student-portal";

export const Route = createFileRoute("/_authenticated/portal/student/")({
  component: StudentDashboard,
});

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl text-navy">{value}</p>
    </div>
  );
}

function StudentDashboard() {
  const fetchDash = useServerFn(getStudentDashboard);
  const q = useQuery({ queryKey: ["student-dashboard"], queryFn: () => fetchDash() });

  if (q.isLoading) return <Loader2 className="h-5 w-5 animate-spin text-navy" />;
  if (!q.data) return <p className="text-sm text-destructive">Couldn't load your dashboard.</p>;

  const { profile, stats, upcoming, certificates, presentations, recentFiles } = q.data;
  const completion = profileCompletion(profile as any);

  return (
    <div className="space-y-6">
      {completion < 100 && (
        <div className="rounded-lg border border-gold/40 bg-gold/5 p-4">
          <p className="text-sm text-navy">
            Your student profile is <strong>{completion}%</strong> complete
            {profile ? ` — ${qualificationLabel(profile.qualification)}` : ""}.
          </p>
          <Link
            to={"/portal/student/profile" as any}
            className="mt-1 inline-block text-sm font-semibold text-navy hover:underline"
          >
            Complete your profile →
          </Link>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Submitted" value={stats.submitted} />
        <Stat label="Drafts" value={stats.drafts} />
        <Stat label="Average mark" value={stats.averageMark === null ? "—" : `${stats.averageMark}%`} />
        <Stat label="Certificates" value={stats.certificates} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-lg text-navy">
            <CalendarClock className="h-4 w-4 text-gold" /> Upcoming deadlines
          </h3>
          {upcoming.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nothing due right now.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {upcoming.map((a: any) => (
                <li key={a.id} className="flex items-center justify-between gap-3">
                  <span className="truncate text-foreground/90">{a.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {a.due_at ? new Date(a.due_at).toLocaleDateString() : "No date"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link
            to={"/portal/student/assignments" as any}
            className="mt-3 inline-block text-sm font-semibold text-navy hover:underline"
          >
            Open assignment centre →
          </Link>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-lg text-navy">
            <FileText className="h-4 w-4 text-gold" /> Recent documents
          </h3>
          {recentFiles.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Your workspace is empty.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {recentFiles.map((f: any) => (
                <li key={f.id} className="flex items-center justify-between gap-3">
                  <span className="truncate text-foreground/90">{f.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {folderLabel(f.folder)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link
            to={"/portal/student/workspace" as any}
            className="mt-3 inline-block text-sm font-semibold text-navy hover:underline"
          >
            Open workspace →
          </Link>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-lg text-navy">
            <Presentation className="h-4 w-4 text-gold" /> Latest class presentations
          </h3>
          {presentations.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No presentations published yet.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {presentations.map((p: any) => (
                <li key={p.id}>
                  <Link
                    to={"/presentations/$slug" as any}
                    params={{ slug: p.slug } as any}
                    className="text-navy hover:underline"
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-lg text-navy">
            <Award className="h-4 w-4 text-gold" /> Certificates
          </h3>
          {certificates.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Complete a course to earn your first certificate.
            </p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {certificates.map((c: any) => (
                <li key={c.id} className="flex items-center justify-between gap-3">
                  <span className="truncate">{c.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <TrendingUp className="h-3.5 w-3.5" /> Late submissions on record: {stats.late}
      </p>
    </div>
  );
}
